import React, { useEffect, useState } from 'react';
import Imgix from 'react-imgix';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import styled, { createGlobalStyle } from 'styled-components';
import useMedia from '../../../hooks/useMedia';
import useScrollDirection from '../../../hooks/useScrollDirection';
import { handleGlobalCart } from '../../../store/actions/index';
import {
  formatGTMDataForProductImpressions,
  gtmTrackEvent
} from '../../../utils/gtmTrackEvent';
import { flatten, getImgixUrl, getSection } from '../../../utils/helper';
import InputSwitch from '../../../common/InputSwitch';
import ProductsJsonLD from '../common/jsonLD/ProductsJsonLD';
import SectionHeader from '../common/sectionHeader/SectionHeader';
import SectionHeaderSkeleton from '../common/sectionHeader/SectionHeaderSkeleton';
import SortBy from '../merchandiser/SortBy';
import FabricSlider from './fabricSlider/FabricSlider';
import FabricSliderSkeleton from './fabricSlider/skeleton/FabricSliderSkeleton';
import FeaturedProducts from './featuredProducts/FeaturedProducts';
import Filter, { DoubleGridIcon, SingleGridIcon } from './filter/Filter';
import FilterSkeleton from './filter/skeleton/FilterSkeleton.js';
import FilterSidebar from './filterSidebar/FilterSidebar';
import useCategoryFiltersBySlug from './hooks/useCategoryFilterBySlug';
import useCategoryProductsBySlug from './hooks/useCategoryProductsBySlug';
import Product from './product/Product';
import ProductSkeleton from './product/skeleton/ProductSkeleton';
import CategorySkeleton from './skeleton/CategorySkeleton';
import { times } from 'lodash';
import {
  segmentTrackEvent
} from '@/utils/segmentTracking';

const Wrapper = styled.div`
  margin-bottom: 80px;
  .container {
    max-width: 1300px !important;
  }

  .product-container {
    display: grid;
    grid-template-columns: ${({ doubleGrid }) =>
      doubleGrid ? '1fr 1fr' : '1fr'};
    grid-gap: 40px;
  }

  /* Styles transition */
  .category-filters-enter {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-enter-active {
    transform: translateY(0);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-exit {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-exit-active {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }

  .category-filters-exit-done {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }

  .grid-buttons-tabletop {
    display: flex;
    justify-content: flex-end;
    margin-right: 20px;
  }

  .first-subtitle {
    margin-bottom: 40px;
    font-size: 35px;
    line-height: 45px;
  }

  @media (max-width: 992px) {
    margin-bottom: 40px;
    .first-subtitle {
      padding: 0 15px;
      margin-top: 35px;
      margin-bottom: 25px;
      font-size: 25px;
      line-height: 30px;
    }

    .filters-wrapper {
      min-height: 70px;
    }
  }
`;

const GlobalStyle = createGlobalStyle`
  html > body {
    overflow: ${({ open }) => (open ? 'hidden' : 'auto')};
    overflow-x: hidden;
  }

  #gorgias-chat-container {
    display: ${({ open }) => (open ? 'none' : 'auto')};
  }

  #nav-banner-mobile {
    visibility: ${({ open }) => (open ? 'hidden' : 'visible')};
    display: ${({ open }) => (open ? 'none' : 'auto')};
    transition: all 1s;
  }


  .nav-banner-container {
    transition: all 0.4s;
    position: ${({ stickyMobile }) => (stickyMobile ? 'absolute' : 'unset')};
    opacity: ${({ stickyMobile }) => (stickyMobile ? '0' : '1')};
    z-index: -1;
  }
`;

const Banner = styled.div`
  background: #ececec;
  padding: 15px;
  .message {
    text-align: center;
    max-width: 718px;
    margin: 0 auto;
  }
`;

const Container = styled.div``;

function Category() {
  const initialSortState = {
    value: null,
    label: 'Featured'
  };
  const match = useRouteMatch();
  const slug = match.params.categorySlug.toLowerCase();
  const displayQuickShip = false;
  const [quickShip, setQuickShip] = useState(false);

  const dispatch = useDispatch();

  const [categoryFilterData, setFilterCategoryData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);

  const [saveCollapsed, setSaveCollapsed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [selectedFabric, setSelectedFabric] = useState(null); // default starting fabric
  const [saveDoubleGrid, setSaveDoubleGrid] = useState(false);
  const [doubleGrid, setDoubleGrid] = useState(true);
  const [colorIds, setColorIds] = useState([]);
  const [patternIds, setPatternIds] = useState([]);
  const [materialIds, setMaterialIds] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [segmentListViewed, setSegmentListViewed] = useState(false);

  const [sortByOptionSelected, setSortByOptionSelected] = useState(
    initialSortState
  );

  const [selectedSort, setSelectedSort] = useState([
    {
      type: sortByOptionSelected.label,
      value: sortByOptionSelected.value
    }
  ]);

  const isMobile =
    typeof window !== `undefined` ? useMedia('(max-width: 992px) ') : null;

  const { loading, error, data } = useCategoryProductsBySlug(
    selectedFabric ? selectedFabric.id : null,
    setDoubleGrid,
    isMobile,
    saveDoubleGrid,
    sortByOptionSelected.value,
    quickShip
  );

  const handleToggleSideCart = ({ sku }) => {
    dispatch(handleGlobalCart({ sku: sku }));
  };

  const {
    loading: loadingFilter,
    error: errorFilter,
    data: dataFilter
  } = useCategoryFiltersBySlug(colorIds, materialIds, patternIds, quickShip);

  const scrollDirection = useScrollDirection();

  const rootMargins = isMobile
    ? '-100px 0px 0px 0px'
    : collapsed
    ? `250px 0px 0px 0px`
    : `250px 0px 0px 0px`;

  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: rootMargins
  });

  useEffect(() => {
    setMaterialIds([]);
    setPatternIds([]);
    setColorIds([]);
    setCategoryData(null);
    setFilterCategoryData(null);
    setSelectedFabric(null);
    setSelectedFilters([]);
    setSegmentListViewed(false);
  }, [match.params.categorySlug]);

  useEffect(() => {
    if (data?.categoryProductsBySlug) {
      //Tracking product list filtered was here before, now moved to separate useEffect

      if (!segmentListViewed) {
        // Tracking Product List Viewed with Segment
        setSegmentListViewed(true);

        const segmentProducts = data.categoryProductsBySlug.subcategories.map((product, i) => {
          return {
            id: product.id,
            name: product.name,
            position: i+1,
            price: product.products[0]?.price,
            url: product.products[0]?.url,
            images: product.products[0]?.images[0].path
          }
        });

        segmentTrackEvent('Product List Viewed', {
          list_id: data.categoryProductsBySlug.id,
          category: data.categoryProductsBySlug.title,
          products: segmentProducts
        });
      }

      setCategoryData(data.categoryProductsBySlug);

      gtmTrackEvent(
        formatGTMDataForProductImpressions(
          flatten(
            data.categoryProductsBySlug.subcategories.map(sub => sub.products)
          ),
          'Category: ' + data.categoryProductsBySlug.name
        )
      );
    }
  }, [slug, loading, data]);


  //START SEGMENT FILTER EVENT

  useEffect(() => {
    const filterTypes = [
      {
        filter: 'colors',
        val: colorIds
      },
      {
        filter: 'fabrics',
        val: selectedFabricInUse ? [selectedFabricInUse.id] : []
      },
      {
        filter: 'materials',
        val: materialIds
      },
      {
        filter: 'patterns',
        val: patternIds
      }
    ];

    const segmentFilters = [];
    filterTypes.forEach(el => {
      el.val.forEach(id => {
        const val = (categoryFilterData ? categoryFilterData[el.filter].find(el => el.id.toString() === id.toString())?.name : null) || dataFilter?.categoryFiltersBySlug[el.filter].find(el => el.id.toString() === id.toString())?.name;

        if (val) {
          segmentFilters.push({
            type: el.filter,
            value: val
          });
        }
      });
    });

    const segmentSort = [
      {
        type: sortByOptionSelected.label,
        value: sortByOptionSelected.value
      }
    ];

    if (JSON.stringify(selectedFilters) !== JSON.stringify(segmentFilters)
      || JSON.stringify(selectedSort) !== JSON.stringify(segmentSort)) {
      // Tracking Product List Filtered with Segment
      setSelectedFilters(segmentFilters);
      setSelectedSort(segmentSort);

      segmentTrackEvent('Product List Filtered', {
        list_id: data?.categoryProductsBySlug.id,
        sorts: segmentSort,
        filters: segmentFilters
      })
    }
  }, [colorIds, patternIds, materialIds, selectedFabricInUse, dataFilter, sortByOptionSelected, data])

  //END SEGMENT PRODUCT LIST FILTERED

  useEffect(() => {
    if (dataFilter) {
      setFilterCategoryData(dataFilter.categoryFiltersBySlug);
    }
  }, [dataFilter, loadingFilter, slug]);

  useEffect(() => {
    if (inView) {
      setCollapsed(false);
    }

    if (saveCollapsed && !inView) {
      setCollapsed(true);
    }
  }, [inView]);

  useEffect(() => {
    if (isMobile) {
      setDoubleGrid(false);
    }
  }, [isMobile]);

  const handleGrid = isDoubleGrid => {
    setDoubleGrid(isDoubleGrid);
    setSaveDoubleGrid(true);
  };

  if (!categoryData)
    return (
      <CategorySkeleton doubleGrid={doubleGrid} setDoubleGrid={setDoubleGrid} />
    );
  if (error) return 'Sorry there was an error.';

  const products = flatten(categoryData.subcategories.map(sub => sub.products));

  const showBannerIn = ['sectional-sofas'];
  const hideFiltersList = ['lampshades'];
  const isInShowBannerIn = showBannerIn.includes(slug);
  const isInHideFiltersList = hideFiltersList.includes(slug);

  const isDiningChairsCategory = slug == 'dining-chairs';
  const isBarCabanaStoolsProducts = slug == 'bar-and-counter-stools';

  const selectedFabricInUse = selectedFabric
    ? selectedFabric
    : categoryData.default_fabric;

  return (
    <Container>
      <Wrapper
        doubleGrid={doubleGrid}
        style={{
          backgroundColor: 'white'
        }}
      >
        <GlobalStyle stickyMobile={!inView && isMobile} open={sliderOpen} />

        <div className="observable" ref={ref}>
          {isInShowBannerIn && (
            <Banner>
              <div className="message">
                Our sofa facility is experiencing supply chain disruptions as it
                adjusts to COVID-19. As a result, this category is temporarily
                unavailable. Here’s the good news: we’re off to the races
                developing new styles that are better than ever. We’ll be back
                soon!
              </div>
            </Banner>
          )}
          {loading ? (
            <SectionHeaderSkeleton showQuizLink />
          ) : (
            <SectionHeader
              title={categoryData.title}
              minimalist={true}
              seoTitle={categoryData.seo_title}
              seoDescription={categoryData.seo_description}
            />
          )}
        </div>

        {isDiningChairsCategory && (
          <h2 className="first-subtitle">Custom Upholstered Dining Chairs</h2>
        )}
        {isBarCabanaStoolsProducts && (
          <h2 className="first-subtitle">
            Custom Upholstered Bar + Counter Stools
          </h2>
        )}

        {isInHideFiltersList ? (
          ''
        ) : (
          <>
            {(selectedFabricInUse && !selectedFabricInUse.id) ||
            isInShowBannerIn ? (
              <div className="grid-buttons-tabletop container">
                <DoubleGridIcon
                  selected={doubleGrid === true}
                  src="/assets/category/double_grid.svg"
                  className="double-grid"
                  onClick={() => handleGrid(true)}
                />
                <SingleGridIcon
                  src="/assets/category/single_grid.svg"
                  className="double-grid"
                  selected={doubleGrid === false}
                  onClick={() => handleGrid(false)}
                />
              </div>
            ) : isMobile ? (
              <>
                {!loadingFilter ? (
                  categoryFilterData?.fabrics?.length ? (
                    <>
                      <MobileFilterWrapper className="hide-on-desktop">
                        <FilterHead
                          onClick={() => {
                            setSliderOpen(true);
                          }}
                          className="container box-shadow"
                        >
                          <div className="fabric-name">
                            {selectedFabricInUse?.image && (
                              <Imgix
                                imgixParams={{ auto: 'compress,format' }}
                                className="fabric-image-head-filter"
                                sizes="40px"
                                src={getImgixUrl(selectedFabricInUse.image)}
                                htmlAttributes={{
                                  alt: selectedFabricInUse.name
                                }}
                              />
                            )}
                            {selectedFabricInUse.name}{' '}
                            {selectedFabricInUse.collaborator &&
                              `by ${selectedFabricInUse.collaborator.name}`}
                          </div>
                          <CircleMoreOptions borderSize={3}>
                            <div className="count">
                              +{categoryFilterData?.fabrics?.length}
                              <br /> More
                            </div>
                          </CircleMoreOptions>
                        </FilterHead>
                      </MobileFilterWrapper>
                      <MobileFilterSidebarContainer>
                        {categoryFilterData && (
                          <CSSTransition
                            in={sliderOpen}
                            timeout={300}
                            classNames="menu-fabric"
                            unmountOnExit
                          >
                            <FilterSidebar
                              setSelectedFabric={setSelectedFabric}
                              selectedFabric={selectedFabricInUse}
                              colors={categoryFilterData.colors}
                              colorIds={colorIds}
                              setColorIds={setColorIds}
                              materials={categoryFilterData.materials}
                              materialIds={materialIds}
                              setMaterialIds={setMaterialIds}
                              setOpen={setSliderOpen}
                              patterns={categoryFilterData.patterns}
                              patternIds={patternIds}
                              setPatternIds={setPatternIds}
                              fabrics={categoryFilterData.fabrics}
                            />
                          </CSSTransition>
                        )}
                      </MobileFilterSidebarContainer>
                    </>
                  ) : (
                    ''
                  )
                ) : (
                  ''
                )}
              </>
            ) : (
              <>
                <DesktopFilterWrapper
                  className="desktop-filters hide-on-mobile"
                  collapsed={collapsed}
                  hideFilters={scrollDirection === 'down' && !inView}
                >
                  <div className="filter-slider-container">
                    <>
                      {/* <FilterSkeleton /> */}

                      {!loadingFilter ? (
                        categoryFilterData?.fabrics?.length ? (
                          <>
                            <Filter
                              materialIds={materialIds}
                              setMaterialIds={setMaterialIds}
                              patternIds={patternIds}
                              setPatternIds={setPatternIds}
                              colorIds={colorIds}
                              setColorIds={setColorIds}
                              patterns={categoryFilterData.patterns}
                              materials={categoryFilterData.materials}
                              colors={categoryFilterData.colors}
                              setDoubleGrid={setDoubleGrid}
                              doubleGrid={doubleGrid}
                              setSaveDoubleGrid={setSaveDoubleGrid}
                            />
                            <FabricSlider
                              selectedFabric={selectedFabricInUse}
                              setSelectedFabric={setSelectedFabric}
                              fabrics={categoryFilterData.fabrics}
                            />
                          </>
                        ) : (
                          ''
                        )
                      ) : (
                        <>
                          <FilterSkeleton />
                          <FabricSliderSkeleton />
                        </>
                      )}
                    </>
                  </div>
                  <CollapseContainer
                    hidden={inView}
                    title={collapsed ? 'Show panel' : 'Hide panel'}
                    collapsed={collapsed}
                    onClick={() => {
                      setCollapsed(!collapsed);
                      setSaveCollapsed(!saveCollapsed);
                    }}
                    className="collapse-container"
                  >
                    <div className="collapse-button">
                      <img
                        src="/assets/pdp/arrow-down.svg"
                        alt="arrow collapse filters"
                      />
                    </div>
                  </CollapseContainer>
                </DesktopFilterWrapper>
              </>
            )}
          </>
        )}

        {(displayQuickShip || categoryData.subcategories.length > 1) && (
          <div className="container mt-4 mb-3 d-flex">
            <div className="flex-grow-1 d-flex align-items-center">
              {displayQuickShip && (
                <div className="d-flex align-items-center">
                  <InputSwitch
                    id="quick-ship-toggle-switch"
                    checked={quickShip}
                    onChange={e => {
                      setQuickShip(e.target.checked);
                    }}
                    round
                  />
                  <QuickShipLabel
                    className="ml-1"
                    htmlFor="quick-ship-toggle-switch"
                  >
                    QUICK SHIP
                  </QuickShipLabel>
                </div>
              )}
            </div>
            <div>
              {categoryData.subcategories.length > 1 && (
                <SortBy
                  selected={sortByOptionSelected}
                  setSelected={setSortByOptionSelected}
                />
              )}
            </div>
          </div>
        )}

        <div className="product-container container mt-4">
          {loading
            ? times(6).map(i => (
                <ProductSkeleton key={i} doubleGrid={doubleGrid} />
              ))
            : categoryData.subcategories.map(subcategory => {
                if (subcategory.products.length === 0) return;
                return (
                  <Product
                    loadingProducts={loading}
                    selectedFabricId={selectedFabricInUse?.id}
                    key={subcategory.id}
                    name={subcategory.name}
                    products={subcategory.products}
                    doubleGrid={doubleGrid}
                    averageRating={subcategory.avg_rating}
                    totalReviews={subcategory.total_reviews}
                    handleToggleSideCart={handleToggleSideCart}
                  />
                );
              })}
        </div>

        <ProductsJsonLD
          name={`Shop ${categoryData.name} in Fabrics You’ll Love`}
          url={`https://theinside.com${getSection(slug)}/${slug}`}
          products={products}
        />
      </Wrapper>
      <FeaturedProducts
        doubleGrid={doubleGrid}
        featuredProducts={categoryData.featuredProducts}
        title={categoryData.featured_products_title}
        handleToggleSideCart={handleToggleSideCart}
      />
    </Container>
  );
}

export default Category;

const CollapseContainer = styled.div`
  transition: all 0.5s;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: calc(50% - 22.5px);
  bottom: -20px;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};

  .collapse-button {
    transition: all 0.3s;
    cursor: pointer;
    background: #ececec;
    width: 45px;
    height: ${({ collapsed }) => (collapsed ? '90px' : '45px')};
    border-radius: 45px;
    display: flex;
    justify-content: center;
    align-items: ${({ collapsed }) => (collapsed ? 'flex-end' : 'center')};

    img {
      transition: all 0.3s;
      transform: ${({ collapsed }) =>
        collapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
      margin-bottom: ${({ collapsed }) => (collapsed ? '5px' : '0px')};
    }
  }
  &:hover {
    margin-bottom: ${({ collapsed }) => (collapsed ? '-25px' : '0')};
  }
`;

const FilterHead = styled.div`
  margin-bottom: 30px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  min-height: 70px;
  border-top: 1px solid #ececec;
  border-bottom: 1px solid #ececec;
  align-items: center;

  &.box-shadow {
    border: 1px solid #ececec;
    box-shadow: 1px 3px 6px 1px rgba(0, 0, 0, 0.1);
    background: #ffffff;
    width: auto;
    margin: 16px;
  }

  .settings-button {
    display: flex;
    align-items: center;
    border-left: 1px solid #ececec;
    padding: 0 25px;
  }
  .fabric-name {
    display: flex;
    align-items: center;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-style: normal;
    font-weight: ${({ theme }) => (theme.old ? 'bold' : 600)};
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #2b2b2b;

    .fabric-image-head-filter {
      margin-right: 10px;
      width: 30px;
      height: 30px;
    }
  }
`;

const CircleMoreOptions = styled.div`
  height: 55px;
  width: 55px;
  min-width: 55px;
  display: block;
  /* width: 100% !important; */
  /* padding-bottom: 100%; */
  position: relative;
  /* max-height: 60px; */
  cursor: pointer;

  .count {
    font-family: ${({ theme }) => theme.fonts.primary};
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #1a1a1a;

    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: all 0.25s ease-in;

    border: 1px solid #dbdbdb;
    box-shadow: inset 0 0 0 3px #fff;

    position: absolute;
    z-index: 2;
    background-color: #fff;
    height: ${({ borderSize }) => `calc(100% - ${borderSize * 2}px)`};
    top: ${({ borderSize }) => `${borderSize}px`};
    bottom: ${({ borderSize }) => `${borderSize}px`};
    left: ${({ borderSize }) => `${borderSize}px`};
    right: ${({ borderSize }) => `${borderSize}px`};
    box-sizing: border-box;
    border: 0;
    width: ${({ borderSize }) => `calc(100% - ${borderSize * 2}px)`};
    border-radius: 50%;

    &:hover {
      background-color: #f1f1f1;
    }
  }

  &:after,
  &:before {
    content: '';
    display: block;
    position: absolute;
    border-radius: inherit;
    transition: all 0.2s linear;
    border-radius: 50%;
  }

  &:before {
    z-index: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      #e78b48 0,
      #ebba4f 16.66667%,
      #377c5e 33.33333%,
      #6b99cc 50%,
      #295caf 66.66667%,
      #a83569 83.33333%,
      #ddd2c5
    );
  }
`;

const MobileFilterWrapper = styled.div`
  position: sticky;
  top: 75px;
  z-index: 8;
  background: white;
`;

const MobileFilterSidebarContainer = styled.div`
  .menu-fabric-enter,
  .menu-view-fabric-enter {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
  .menu-fabric-enter-active {
    transform: translateX(0);
    transition: transform 0.3s ease-in-out;
  }
  .menu-fabric-exit {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
  .menu-fabric-exit-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
`;

const DesktopFilterWrapper = styled.div`
  top: 0px;
  position: sticky;
  width: 100%;
  height: auto;
  background: white;
  z-index: 8;
  transition: transform 0.3s ease-in-out;

  transform: ${({ hideFilters, collapsed }) =>
    collapsed
      ? 'translateY(-100%)'
      : hideFilters
      ? 'translateY(-35%)'
      : 'translateY(0%)'};

  /* .category-filters-enter {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-enter-active {
    transform: translateY(0);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-exit {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }
  .category-filters-exit-active {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  }

  .category-filters-exit-done {
    transform: translateY(-100%);
    transition: transform 0.3s ease-in-out;
  } */
`;

const QuickShipLabel = styled.label`
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 10%;
  margin-bottom: 0;

  @media (min-width: 992px) {
    font-size: 13px;
  }
`;

